import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { UserNav } from "../../components/dashboard/user-nav";
import { rapportsApi } from "../../api/rapports.api";
import { useAuth } from "../../context/AuthContext";
import { Badge } from "../../components/ui/badge";
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
    CommandSeparator,
} from "../../components/ui/command";
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "../../components/ui/popover";
import {
    Bell,
    Calculator,
    Calendar,
    CreditCard,
    Settings,
    Smile,
    User,
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { cn } from "../../lib/utils";

export function Topbar() {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);
    const [pendingCount, setPendingCount] = useState(0);

    const { user } = useAuth();
    const isAdmin = user?.role === 'Admin' || user?.role === 'SuperAdmin';

    useEffect(() => {
        const fetchPendingCount = async () => {
            if (!isAdmin) return;
            
            try {
                const { pending_count } = await rapportsApi.getPendingCount();
                setPendingCount(pending_count);
            } catch (error) {
                console.error("Failed to fetch pending report count", error);
            }
        };

        fetchPendingCount();
        const interval = setInterval(fetchPendingCount, 60000); // Rafraîchir chaque minute
        return () => clearInterval(interval);
    }, [isAdmin]);

    useEffect(() => {
        const down = (e: KeyboardEvent) => {
            if (e.key === "k" && (e.metaKey || e.ctrlKey)) {
                e.preventDefault();
                setOpen((open) => !open);
            }
        }
        document.addEventListener("keydown", down)
        return () => document.removeEventListener("keydown", down)
    }, [])

    return (
        <div className="border-b">
            <div className="flex h-16 items-center px-4 justify-between">
                <div className="flex-1" />
                <div className="flex-1 flex justify-center">
                    <Popover open={open} onOpenChange={setOpen}>
                        <PopoverTrigger asChild>
                            <Button
                                variant="outline"
                                className={cn(
                                    "relative w-full max-w-lg justify-start text-sm text-muted-foreground sm:pr-12 md:w-40 lg:w-[500px] hover:bg-background hover:text-muted-foreground"
                                )}
                            >
                                <span className="hidden lg:inline-flex">{t('dashboard:topbar.search')}</span>
                                <span className="inline-flex lg:hidden">{t('dashboard:topbar.search')}</span>
                                <kbd className="pointer-events-none absolute right-1.5 top-1.5 hidden h-5 select-none items-center gap-1 rounded border bg-muted px-1.5 font-mono text-[10px] font-medium opacity-100 sm:flex">
                                    <span className="text-xs">Ctrl</span>K
                                </kbd>
                            </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-[500px] p-0" align="start">
                            <Command>
                                <CommandInput placeholder={t('dashboard:topbar.searchPlaceholder')} />
                                <CommandList>
                                    <CommandEmpty>{t('dashboard:topbar.noResults')}</CommandEmpty>
                                    <CommandGroup heading={t('dashboard:topbar.suggestions')}>
                                        <CommandItem>
                                            <Calendar className="mr-2 h-4 w-4" />
                                            <span>{t('dashboard:topbar.calendar')}</span>
                                        </CommandItem>
                                        <CommandItem>
                                            <Smile className="mr-2 h-4 w-4" />
                                            <span>{t('dashboard:topbar.emoji')}</span>
                                        </CommandItem>
                                        <CommandItem>
                                            <Calculator className="mr-2 h-4 w-4" />
                                            <span>{t('dashboard:topbar.calculator')}</span>
                                        </CommandItem>
                                    </CommandGroup>
                                    <CommandSeparator />
                                    <CommandGroup heading={t('dashboard:topbar.settings')}>
                                        <CommandItem>
                                            <User className="mr-2 h-4 w-4" />
                                            <span>{t('dashboard:topbar.profile')}</span>
                                            <span className="ml-auto text-xs tracking-widest text-muted-foreground">⌘P</span>
                                        </CommandItem>
                                        <CommandItem>
                                            <CreditCard className="mr-2 h-4 w-4" />
                                            <span>{t('dashboard:topbar.billing')}</span>
                                            <span className="ml-auto text-xs tracking-widest text-muted-foreground">⌘B</span>
                                        </CommandItem>
                                        <CommandItem>
                                            <Settings className="mr-2 h-4 w-4" />
                                            <span>{t('dashboard:topbar.settings')}</span>
                                            <span className="ml-auto text-xs tracking-widest text-muted-foreground">⌘S</span>
                                        </CommandItem>
                                    </CommandGroup>
                                </CommandList>
                            </Command>
                        </PopoverContent>
                    </Popover>
                </div>
                <div className="flex items-center space-x-4 flex-1 justify-end">
                    {pendingCount > 0 && (
                        <Link to="/administration/rapports" title={t('dashboard:topbar.notifications', { count: pendingCount })}>
                            <Button variant="ghost" size="icon" className="relative group">
                                <Bell className="h-5 w-5 text-muted-foreground group-hover:text-primary transition-colors" />
                                <Badge className="absolute -top-1 -right-1 px-1.5 min-w-[1.25rem] h-5 flex items-center justify-center bg-red-500 hover:bg-red-600 border-2 border-background text-[10px] font-bold text-white shadow-sm">
                                    {pendingCount > 99 ? '99+' : pendingCount}
                                </Badge>
                            </Button>
                        </Link>
                    )}
                    <UserNav />
                </div>
            </div>
        </div>
    );
}
